import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Circle,
  Image
} from '@chakra-ui/react';
import { FaCoins } from 'react-icons/fa';
import { CASH_KING_CONFIG } from '../Games/ScratchCardConfigs';

/**
 * CashKingCard Component
 * 
 * A dedicated component for rendering the CASH KING scratch card
 * with its specific 3x4 grid layout and green color scheme.
 */
const CashKingCard = ({ 
  isPreview = false, 
  onPlay,
  onBuy,
  gameState = null 
}) => {
  const [cells, setCells] = useState(
    Array(12).fill().map((_, i) => ({ 
      id: i, 
      revealed: false, // Don't auto-reveal in preview mode
      value: null,
      isWinning: false
    }))
  );
  
  // Handle cell click (scratch)
  const handleCellClick = (index) => {
    if (isPreview || !gameState || gameState.status !== 'active') return;
    
    const newCells = [...cells];
    newCells[index].revealed = true;
    
    // In a real game, this would come from the backend
    if (!newCells[index].value && gameState.cellValues) {
      newCells[index].value = gameState.cellValues[index];
      newCells[index].isWinning = gameState.winningCells?.includes(index);
    }
    
    setCells(newCells);
  };
  
  // Render a cell
  const renderCell = (index) => {
    const cell = cells[index];
    const isRevealed = cell.revealed;
    
    return (
      <Box
        key={index}
        bg="#ffc107" // Bright yellow background for cells
        border="1px solid"
        borderColor="#e6a800"
        borderRadius="md"
        height="65px"
        width="65px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor={!isPreview && !isRevealed ? "pointer" : "default"}
        onClick={() => handleCellClick(index)}
        position="relative"
        transition="all 0.2s"
        _hover={!isPreview && !isRevealed ? { transform: "scale(1.05)" } : {}}
      >
        {isRevealed ? (
          <Box
            width="80%"
            height="80%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="xl" color="white" fontWeight="bold">{cell.value || '0'}</Text>
          </Box>
        ) : (
          <Box 
            position="absolute" 
            top="0" 
            left="0" 
            right="0" 
            bottom="0" 
            bg="#ffc107"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={1}
          >
            <Image src="/images/777.png" height="80%" alt="777 Jackpot" />
          </Box>
        )}
      </Box>
    );
  };
  
  // Render the grid
  const renderGrid = () => {
    return (
      <Box
        bg="rgba(0, 40, 20, 0.8)" // Matching the bounty pool box color
        borderRadius="md"
        p={3}
        width="100%"
        maxW="100%"
        minH="280px"
        mx="auto"
        my={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        border="1px solid rgba(0, 255, 128, 0.2)"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
      >
        <Box
          width="100%"
          display="grid"
          gridTemplateColumns="repeat(4, 1fr)"
          gridTemplateRows="repeat(3, 1fr)"
          gap="10px"
          justifyItems="center"
          alignItems="center"
          maxW="300px"
          mx="auto"
        >
          {Array(12).fill().map((_, i) => (
            <Box key={i}>
              {renderCell(i)}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };
  
  return (
    <Box 
      position="relative" 
      bg="rgba(0, 30, 15, 0.95)" // Matching the bounty pool background
      p={8} 
      borderRadius="lg" 
      boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
      border="1px solid rgba(0, 255, 128, 0.15)"
      backdropFilter="blur(10px)"
      width="400px"
      height="650px"
      maxW="100%"
      mx="0"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      {/* Cost badge */}
      <Box 
        position="absolute" 
        top="10px" 
        left="10px" 
        bg="rgba(0,0,0,0.7)" 
        color="yellow.300" 
        px={2} 
        py={1} 
        borderRadius="md"
        fontWeight="bold"
        display="flex"
        alignItems="center"
        gap={1}
      >
        <FaCoins /> {CASH_KING_CONFIG.cost} JINU
      </Box>
      
      {/* Creator badge if applicable */}
      <Box 
        position="absolute" 
        top="10px" 
        right="10px" 
        bg="rgba(0,0,0,0.7)" 
        color="purple.300" 
        px={2} 
        py={1} 
        borderRadius="md"
        fontWeight="bold"
        display="flex"
        alignItems="center"
        gap={1}
      >
        👑 Creator: 20%
      </Box>
      
      {/* Card header - Title and description */}
      <VStack spacing={1} mt={12} mb={2}>
        <Heading 
          color="yellow.300" 
          fontWeight="bold" 
          fontSize="3xl"
          textAlign="center"
        >
          {CASH_KING_CONFIG.name}
        </Heading>
        <Text 
          color="white" 
          fontWeight="bold" 
          fontSize="md"
          textAlign="center"
        >
          {CASH_KING_CONFIG.description}
        </Text>
      </VStack>
      
      {/* Winning Symbols Display */}
      <Box textAlign="center" mt={4} mb={4}>
        <HStack justify="center" spacing={4}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Circle 
              key={i} 
              size="50px" 
              bg="#0d3b1d" 
              color="yellow.300"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.2)"
            >
              <Text fontSize="2xl">💎</Text>
            </Circle>
          ))}
        </HStack>
        <Text color="#4fd1c5" mt={2} mb={1} fontSize="md" fontWeight="medium">Winning Symbols</Text>
      </Box>
      
      {/* Card grid */}
      {renderGrid()}
      
      {/* Match instructions */}
      <Text 
        color="white" 
        fontSize="sm"
        textAlign="center" 
        mt={2}
        mb={2}
        fontWeight="medium"
      >
        Match any of the winning symbols to win their prize value.
      </Text>
    </Box>
  );
};

export default CashKingCard;
